import React from 'react'
import styled, { css } from 'styled-components'
import { useTranslation } from 'react-i18next'

// Components
import Text from '../Text'
import Link from '../Link'
import Dropdown from '../formControl/Dropdown'

// Utils
import useReleaseNotes from '../../hooks/useReleaseNotes'

// Styles
import { hsla } from '../../styles/colors'
import { fastTransition, shadows } from '../../styles/effects'
import { mqFrom } from '../../styles/mediaQueries'

// Types
import {
  IReleaseNote,
  ReleaseNoteAudience,
} from '../../common/models/ReleaseNote'

interface IProps {
  audience?: ReleaseNoteAudience
  notes: IReleaseNote[]
  unreadIds: string[]
  selectedId?: string
  // Collapsed by default; opened below L it replaces the note
  isOpen: boolean
  onSelect: (noteId: string) => void
  onChangeAudience: (audience: ReleaseNoteAudience) => void
}

const audienceLabelKeys: Record<ReleaseNoteAudience, string> = {
  [ReleaseNoteAudience.ADMIN]: 'general.users.admin',
  [ReleaseNoteAudience.LEAD]: 'general.users.lead',
  [ReleaseNoteAudience.EMPLOYEE]: 'evaluationFormPdf.employee',
}

// Left column of the "What's new" dialog: notes grouped per month with an
// unread dot, the member's unread count, a role switch for roles below the
// member's own, and "Mark all as read". Collapsed until the top-bar toggle
// opens it; below L it replaces the note.
const ReleaseNotesList: React.FC<IProps> = ({
  audience,
  notes,
  unreadIds,
  selectedId,
  isOpen,
  onSelect,
  onChangeAudience,
}) => {
  const { t } = useTranslation()
  const { audiences, language, contentOf, markAllRead } = useReleaseNotes(
    false,
    audience
  )
  const monthOf = (note: IReleaseNote) =>
    note.draft
      ? t('releaseNotes.draft')
      : new Date(note.publishedAt).toLocaleDateString(language, {
          month: 'long',
          year: 'numeric',
        })

  return (
    <Column $isOpen={isOpen}>
      {(!!unreadIds.length || audiences.length > 1) && (
        <Header>
          {!!unreadIds.length && (
            <Text textStyle="label" color="textLight">
              {t('releaseNotes.unread', { count: unreadIds.length })}
            </Text>
          )}
          {audiences.length > 1 && (
            <Dropdown
              value={audience}
              onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                onChangeAudience(event.target.value as ReleaseNoteAudience)
              }
            >
              {audiences.map((option) => (
                <option key={option} value={option}>
                  {t(audienceLabelKeys[option])}
                </option>
              ))}
            </Dropdown>
          )}
        </Header>
      )}

      <List>
        {notes.map((note, i) => {
          const month = monthOf(note)
          const unread = unreadIds.includes(note.id)
          const selected = note.id === selectedId
          return (
            <React.Fragment key={note.id}>
              {(i === 0 || monthOf(notes[i - 1]) !== month) && (
                <MonthLabel
                  textStyle="labelSmallCaps"
                  color="text72"
                  $isFirst={i === 0}
                >
                  {month}
                </MonthLabel>
              )}
              <ListItem
                type="button"
                $selected={selected}
                aria-current={selected}
                onClick={() => onSelect(note.id)}
              >
                <ItemLabel
                  textStyle={unread ? 'labelHeavy' : 'label'}
                  color="textDark"
                >
                  {contentOf(note)?.label}
                </ItemLabel>
                {unread && <UnreadDot />}
              </ListItem>
            </React.Fragment>
          )
        })}
      </List>

      {!!unreadIds.length && (
        <Footer>
          <Link
            caption={t('releaseNotes.markAllRead')}
            color="primary"
            disableUnderline
            onClick={markAllRead}
          />
        </Footer>
      )}
    </Column>
  )
}

export default ReleaseNotesList

const Column = styled.div<{ $isOpen: boolean }>`
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  flex-direction: column;
  width: 100%;
  min-height: 0;
  background-color: ${({ theme }) => theme.colors.primaryLight};
  border-right: 1px solid ${({ theme }) => theme.colors.textLightest};

  ${mqFrom.L`
    flex-shrink: 0;
    width: 296px;
  `}
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex-shrink: 0;
  padding: 24px 16px 12px;

  ${mqFrom.L`
    padding: 24px 24px 12px;
  `}
`

const List = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 8px 8px 16px;
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme.colors.textLighter} transparent;

  ${mqFrom.L`
    padding: 8px 12px 16px;
  `}
`

const MonthLabel = styled(Text)<{ $isFirst: boolean }>`
  flex-shrink: 0;
  padding: ${({ $isFirst }) => ($isFirst ? '4px' : '12px')} 12px 4px;
`

const ListItem = styled.button<{ $selected: boolean }>`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 36px;
  padding: 8px 12px;
  border: none;
  border-radius: 8px;
  background-color: transparent;
  text-align: left;
  cursor: pointer;
  transition: background-color ${fastTransition}, box-shadow ${fastTransition};

  @media (hover: hover) {
    &:hover {
      background-color: ${({ theme }) => hsla(theme.colors.lightest, 0.64)};
    }
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }

  ${({ $selected, theme }) =>
    $selected &&
    css`
      &,
      &:hover {
        background-color: ${theme.colors.lightest};
        box-shadow: ${shadows.depthTwo};
      }
    `}
`

const ItemLabel = styled(Text)`
  flex-grow: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const UnreadDot = styled.span`
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.primary};
`

const Footer = styled.div`
  flex-shrink: 0;
  padding: 16px 24px;
  padding-bottom: max(16px, env(safe-area-inset-bottom));
  border-top: 1px solid ${({ theme }) => theme.colors.textLightest};
`
